import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { ErrorText } from "../components/ui/feedback";
import { Field } from "../components/ui/Field";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { adminApi, type WorkspacePatch } from "./api";
import type { AdminWorkspace } from "./types";

export interface EditWorkspaceModalProps {
  workspace: AdminWorkspace;
  onClose: () => void;
  onSaved: () => void;
}

/** Parses a non-negative whole number from a text field, or null when it is not one. */
export function parseWholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/** Edits name, plan and the two quotas of one workspace. */
export function EditWorkspaceModal({ workspace, onClose, onSaved }: EditWorkspaceModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState(workspace.name);
  const [plan, setPlan] = useState(workspace.plan);
  const [quotaBytes, setQuotaBytes] = useState(String(workspace.quotaBytes));
  const [quotaPersons, setQuotaPersons] = useState(String(workspace.quotaPersons));
  const [invalid, setInvalid] = useState(false);

  const save = useMutation({
    mutationFn: (patch: WorkspacePatch) => adminApi.updateWorkspace(workspace.id, patch),
    onSuccess: onSaved,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const bytes = parseWholeNumber(quotaBytes);
    const persons = parseWholeNumber(quotaPersons);
    if (bytes === null || persons === null || !name.trim() || !plan.trim()) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    save.mutate({
      name: name.trim(),
      plan: plan.trim(),
      quota_bytes: bytes,
      quota_persons: persons,
    });
  };

  return (
    <Modal title={t("admin.edit.title")} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("admin.edit.name")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("admin.edit.plan")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={plan}
              onChange={(event) => setPlan(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("admin.edit.quotaBytes")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              inputMode="numeric"
              className="tabular-nums"
              value={quotaBytes}
              onChange={(event) => setQuotaBytes(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("admin.edit.quotaPersons")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              inputMode="numeric"
              className="tabular-nums"
              value={quotaPersons}
              onChange={(event) => setQuotaPersons(event.target.value)}
            />
          )}
        </Field>
        {invalid ? <ErrorText error={null}>{t("error.validation_error")}</ErrorText> : null}
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose} disabled={save.isPending}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
